/**
 * Invite link inspector — paste a private-comp invite token, see what comp it
 * belongs to, who created it, and (when tracked) attribution counts.
 *
 * Today the share / signup / entry attribution counts return null because we
 * don't yet capture those on the invite token. The drawer surfaces them as
 * `—` so the absence is visible rather than silently zero.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

import { useAdminInvite } from './api';
import { DASH, formatTimestamp } from './shared';

export function InviteInspectorTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tokenParam = searchParams.get('token') ?? '';
  const [input, setInput] = useState(tokenParam);

  // Sync the input when the URL token changes (e.g. browser back).
  useEffect(() => {
    setInput(tokenParam);
  }, [tokenParam]);

  const { data, isLoading, error } = useAdminInvite(tokenParam || null);

  function handleSubmit() {
    const next = new URLSearchParams(searchParams);
    if (input.trim()) next.set('token', input.trim());
    else next.delete('token');
    setSearchParams(next, { replace: true });
  }

  const goCompetition = () => {
    if (!data) return;
    const next = new URLSearchParams();
    next.set('tab', 'all');
    next.set('id', data.competition.id);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6">
      <form
        className="flex max-w-xl items-end gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit();
        }}
      >
        <div className="flex-1">
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Invite token
          </label>
          <Input
            placeholder="haaland-saka-palmer"
            value={input}
            onChange={(e) => setInput(e.target.value)}
          />
        </div>
        <Button type="submit">Inspect</Button>
      </form>

      {!tokenParam ? (
        <div className="rounded-md border border-dashed bg-muted/20 px-4 py-12 text-center text-sm text-muted-foreground">
          Paste a private-competition invite token above to see who created it and where it leads.
        </div>
      ) : isLoading ? (
        <div className="rounded-md border bg-card px-4 py-12 text-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : error ? (
        <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-6 text-sm text-destructive">
          {error instanceof Error ? error.message : 'Lookup failed'}
        </div>
      ) : data ? (
        <div className="space-y-6">
          <Section title="Token">
            <Row
              label="Value"
              value={<code className="text-xs">{data.token}</code>}
            />
          </Section>

          <Section title="Competition">
            <Row label="Title" value={data.competition.title} />
            <Row
              label="ID"
              value={<code className="text-xs">{data.competition.id}</code>}
            />
            <Row label="Slug" value={data.competition.slug ?? DASH} />
            <Row label="Status" value={<Badge variant="outline">{data.competition.status}</Badge>} />
            <Row label="Format" value={data.competition.format} />
            <Row label="Round window" value={data.competition.round_window} />
            <Row label="Created at" value={formatTimestamp(data.competition.created_at)} />
            <Row
              label="Entry deadline"
              value={
                data.competition.entry_deadline
                  ? formatTimestamp(data.competition.entry_deadline)
                  : DASH
              }
            />
            <Row label="Entrant count" value={data.competition.entrant_count.toString()} />
            <div className="pt-2">
              <Button type="button" variant="outline" size="sm" onClick={goCompetition}>
                Open competition →
              </Button>
            </div>
          </Section>

          <Section title="Creator">
            {data.creator ? (
              <>
                <Row label="Kind" value={data.creator.kind} />
                <Row
                  label={data.creator.kind === 'user' ? 'Email' : 'Agent name'}
                  value={data.creator.email ?? data.creator.name ?? DASH}
                />
                <Row
                  label="ID"
                  value={<code className="text-xs">{data.creator.id}</code>}
                />
              </>
            ) : (
              <p className="text-sm text-muted-foreground">No creator on file.</p>
            )}
          </Section>

          <Section title="Attribution">
            <Row
              label="Shares"
              value={data.shares_count == null ? DASH : data.shares_count.toLocaleString()}
            />
            <Row
              label="Signups attributed"
              value={
                data.signups_attributed == null ? DASH : data.signups_attributed.toLocaleString()
              }
            />
            <Row
              label="Entries attributed"
              value={
                data.entries_attributed == null ? DASH : data.entries_attributed.toLocaleString()
              }
            />
            <p className="pt-1 text-xs text-muted-foreground">
              Attribution counts are not tracked yet — they will populate once share-link analytics
              ships.
            </p>
          </Section>
        </div>
      ) : null}
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border bg-card px-4 py-3">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right">{value}</span>
    </div>
  );
}
